import { DragHandle } from '@mui/icons-material';
import { Box, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import BTTextField from '../atoms/text-field/bt-text-field';
import { Poll } from '@extension/context/content/components/add-scheduled-message-button/schedule-message-types';
import { useAppDispatch, useAppSelector } from '@/common/reducers/hooks/store.hook';
import { setPoll } from '@common/reducers/ScheduledMessageEditorReducer';
import TemplatePlaceholderBar from './template-placeholder-bar';

import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';

// Types
interface PollOption {
    id: number;
    text: string;
    order: number;
}

interface CreatePollProps {
    disabled?: boolean;
}

// Constants
const MIN_OPTIONS = 2;
const RANDOM_ID_MULTIPLIER = 1000000000000000000000000;

// Styles
const styles = {
    container: {
        width: '100%',
        padding: '20px',
        boxSizing: 'border-box',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        border: '1px solid var(--tasks-cell-border)',
        backgroundColor: 'var(--color-surface)',
    },
    sectionTitle: {
        color: 'var(--color-primary)',
        fontWeight: 500,
        fontSize: '14px',
    },
    questionField: {
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'var(--color-surface)',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '15px',
            '& fieldset': {
                border: 'none',
            },
            '&:hover': {
                backgroundColor: 'var(--color-surface)',
                '& fieldset': {
                    border: 'none',
                },
            },
            '&.Mui-focused': {
                backgroundColor: 'var(--color-surface)',
                '& fieldset': {
                    border: 'none',
                },
                boxShadow: '0 0 0 1px #6b7280',
            },
        },
        '& .MuiOutlinedInput-input': {
            padding: '14px 16px',
            color: 'var(--color-primary)',
            '&::placeholder': {
                color: '#9ca3af',
                opacity: 1,
            },
        },
    },
    optionsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        maxHeight: '200px',
        overflowY: 'auto',
        overflowX: 'hidden',
        '&::-webkit-scrollbar': {
            width: '6px',
        },
        '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '4px',
            '&:hover': {
                background: '#a8a8a8',
            }
        }
    },
    optionWrapper: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '0',
    },
    optionInput: (isError: boolean) => ({
        flex: 1,
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        padding: '4px 8px',
        fontSize: '14px',
        color: isError ? '#dc2626' : 'var(--color-primary)',
        backgroundColor: 'var(--color-surface)',
        outline: 'none',
        fontFamily: 'inherit',
        transition: 'all 0.2s ease',
        height: '30px',
        '&::placeholder': {
            color: '#9ca3af',
        },
        '&:hover': {
            borderColor: '#9ca3af',
        },
        '&:focus': {
            border: '1px solid #51b3e2 !important',
            backgroundColor: 'var(--color-surface)',
        },
        '&:disabled': {
            backgroundColor: '#f9fafb',
            borderColor: 'var(--color-border)',
            color: '#d1d5db',
            cursor: 'not-allowed',
        },
    }),
    dragHandle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '20px',
        height: '20px',
        cursor: 'grab',
        color: '#d1d5db',
        opacity: 0,
        transition: 'opacity 0.2s ease',
        '&:hover': {
            color: '#9ca3af',
        },
        '&:active': {
            cursor: 'grabbing',
        },
    },
    optionContainer: {
        '&:hover .drag-handle': {
            opacity: 1,
        },
    },
    settingsSection: {
        marginTop: '5px !important',
    },

    switch: {
        '& .MuiSwitch-switchBase': {
            color: 'var(--color-surface)',
            '&.Mui-checked': {
                color: 'var(--color-surface)',
                '& + .MuiSwitch-track': {
                    backgroundColor: 'var(--color-primary)',
                    opacity: 1,
                },
            },
        },
        '& .MuiSwitch-track': {
            backgroundColor: 'var(--color-border)',
            opacity: 1,
        },
    },
    switchLabel: {
        color: 'var(--color-primary)',
        fontSize: '14px',
        fontWeight: 400,
        margin: 0,
        '& .MuiFormControlLabel-label': {
            fontSize: '14px',
            color: 'var(--color-primary)',
        },
    },
    switchTypography: {
        fontSize: '14px',
        color: '#374151',
    },
    errorText: {
        color: '#dc2626',
        fontSize: '12px',
        fontWeight: 500,
        marginTop: '8px',
    },
};

// Memoized Sub-Components
const QuestionInput = memo(({
    question,
    onChange,
    onFieldFocus,
    disabled
}: {
    question: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onFieldFocus?: (el: HTMLInputElement) => void;
    disabled?: boolean;
}) => {
    const { t } = useTranslation('common');
    // Bound to both onFocus and onSelect, whose element generics differ, so the
    // param is the un-parametrized SyntheticEvent (supertype of both); the actual
    // target is narrowed at the call.
    const capture = (e: React.SyntheticEvent) => onFieldFocus?.(e.target as HTMLInputElement);
    return (
    <Box>
        <Typography sx={styles.sectionTitle}>
            {t('poll.question')}
        </Typography>
        <BTTextField
            fullWidth
            value={question}
            onChange={onChange}
            onFocus={capture}
            onSelect={capture}
            placeholder={t('poll.questionPlaceholder')}
            sx={styles.questionField}
            disabled={disabled}
        />
    </Box>
    );
});

QuestionInput.displayName = 'QuestionInput';

const PollOption = ({
    option,
    index,
    isDuplicate,
    onOptionChange,
    onOptionFocus,
    disabled
}: {
    option: PollOption;
    index: number;
    isDuplicate: boolean;
    onOptionChange: (index: number, value: string) => void;
    onOptionFocus?: (index: number, el: HTMLInputElement) => void;
    disabled?: boolean;
}) => {
    const { t } = useTranslation('common');
    const capture = (e: React.SyntheticEvent<HTMLInputElement>) => onOptionFocus?.(index, e.target as HTMLInputElement);
    return (
        <Draggable
            draggableId={`option-${option.id}`}
            index={index}
        >
            {(provided, snapshot) => (
                <Box
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    sx={styles.optionContainer}
                >
                    <Box sx={styles.optionWrapper}>
                        <input
                            type="text"
                            value={option.text}
                            onChange={(e) => onOptionChange(index, e.target.value)}
                            onFocus={capture}
                            onSelect={capture}
                            placeholder={t('poll.optionPlaceholder', { index: index + 1 })}
                            style={styles.optionInput(isDuplicate)}
                            disabled={disabled}
                        />
                        <Box
                            {...provided.dragHandleProps}
                            sx={styles.dragHandle}
                            className="drag-handle"
                        >
                            <DragHandle sx={{ fontSize: '14px' }} />
                        </Box>
                    </Box>
                </Box>
            )}
        </Draggable>
    );
};

PollOption.displayName = 'PollOption';

const OptionsContainer = memo(({
    options,
    duplicateIndices,
    onOptionChange,
    onOptionFocus,
    onDragEnd,
    disabled
}: {
    options: PollOption[];
    duplicateIndices: Set<number>;
    onOptionChange: (index: number, value: string) => void;
    onOptionFocus?: (index: number, el: HTMLInputElement) => void;
    onDragEnd: (result: DropResult) => void;
    disabled?: boolean;
}) => {
    const { t } = useTranslation('common');
    return (
        <Box>
            <Typography sx={styles.sectionTitle}>
                {t('poll.options')}
            </Typography>
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="options" direction="vertical">
                    {(provided) => (
                        <Box
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            sx={styles.optionsContainer}
                        >
                            {options.map((option, index) => (
                                <PollOption
                                    key={option.id}
                                    option={option}
                                    index={index}
                                    isDuplicate={duplicateIndices.has(index)}
                                    onOptionChange={onOptionChange}
                                    onOptionFocus={onOptionFocus}
                                    disabled={disabled}
                                />
                            ))}
                            {provided.placeholder}
                        </Box>
                    )}
                </Droppable>
            </DragDropContext>
        </Box>
    );
});

OptionsContainer.displayName = 'OptionsContainer';

const Footer = memo(({
    allowMultiple,
    hasDuplicates,
    onAllowMultipleChange,
    disabled
}: {
    allowMultiple: boolean;
    hasDuplicates: boolean;
    onAllowMultipleChange: () => void;
    disabled?: boolean;
    }) => {
    const { t } = useTranslation('common');
    return (
    <Box sx={styles.settingsSection}>
        <FormControlLabel
            control={
                <Switch
                    checked={allowMultiple}
                    onChange={onAllowMultipleChange}
                    sx={styles.switch}
                    disabled={disabled}
                />
            }
            label={t('poll.allowMultipleAnswers')}
            sx={styles.switchLabel}
            componentsProps={{
                typography: { sx: styles.switchTypography }
            }}
        />
        {hasDuplicates && (
            <Typography sx={styles.errorText}>
                {t('poll.uniqueOptionsError')}
            </Typography>
        )}
    </Box>
    );
});

Footer.displayName = 'Footer';

const CreatePoll: React.FC<CreatePollProps> = ({
    disabled = false,
}) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation('common');
    
    // Read poll state directly from Redux
    const poll = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.poll);
    const pollFromWAModal = useAppSelector(state => state.AddScheduledMessageButtonReducer.pollFromWAModal);
    // Variables/placeholders are a campaign-template feature; only show the bar there.
    const editorContext = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.editorContext);

    // Memoized values
    const options = useMemo(() => poll.options || [], [poll.options]);
    const question = useMemo(() => poll.question || '', [poll.question]);
    const allowMultiple = useMemo(() => poll.allowMultiple || false, [poll.allowMultiple]);

    const duplicateIndices = useMemo(() => {
        const indices = new Set<number>();
        options.forEach((option: PollOption, index: number) => {
            if (option.text !== '' && options.some((o: PollOption, i: number) =>
                i !== index &&
                o.text !== '' &&
                o.text === option.text
            )) {
                indices.add(index);
            }
        });
        return indices;
    }, [options]);

    const hasDuplicateOptions = useMemo(() => duplicateIndices.size > 0, [duplicateIndices]);

    // Handlers
    const handleSetLocalPollState = useCallback((newPollState: Partial<Poll> | ((prev: Poll) => Partial<Poll>)) => {
        if (typeof newPollState === 'function') {
            dispatch(setPoll(newPollState(poll)));
        } else {
            dispatch(setPoll(newPollState));
        }
    }, [dispatch, poll]);

    const handleQuestionChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (!handleSetLocalPollState) return;

        handleSetLocalPollState((prev: Poll) => ({
            ...prev,
            question: e.target.value
        }));
    }, [handleSetLocalPollState]);

    const handleOptionChange = useCallback((index: number, value: string) => {
        if (!handleSetLocalPollState) return;

        handleSetLocalPollState((prev: Poll) => {
            const newOptions = [...(prev.options || [])];
            newOptions[index] = { ...newOptions[index], text: value };

            if (value === '' && newOptions.length > MIN_OPTIONS) {
                newOptions.splice(index, 1);
                return { ...prev, options: newOptions };
            }

            if (index === newOptions.length - 1 && value !== '') {
                newOptions.push({
                    id: Math.floor(Math.random() * RANDOM_ID_MULTIPLIER),
                    text: '',
                    order: newOptions.length
                });
            }

            return { ...prev, options: newOptions };
        });
    }, [handleSetLocalPollState]);

    const handleDragEnd = useCallback((result: DropResult) => {
        if (!result.destination || !handleSetLocalPollState) return;

        if (result.source.index === result.destination.index) return;

        handleSetLocalPollState((prev: Poll) => {
            const newOptions = [...(prev.options || [])];
            const [movedOption] = newOptions.splice(result.source.index, 1);
            if (!result.destination) return prev;
            newOptions.splice(result.destination.index, 0, movedOption);
            return { ...prev, options: newOptions };
        });
    }, [handleSetLocalPollState]);

    const handleAllowMultipleChange = useCallback(() => {
        if (!handleSetLocalPollState) return;

        handleSetLocalPollState((prev: Poll) => ({
            ...prev,
            allowMultiple: !prev.allowMultiple
        }));
    }, [handleSetLocalPollState]);

    // Track the focused poll field (+ caret) so a clicked variable inserts at the cursor.
    const activeFieldRef = useRef<{ kind: 'question' | 'option'; index: number; el: HTMLInputElement | null }>({
        kind: 'question', index: -1, el: null,
    });
    const captureQuestion = useCallback((el: HTMLInputElement) => {
        activeFieldRef.current = { kind: 'question', index: -1, el };
    }, []);
    const captureOption = useCallback((index: number, el: HTMLInputElement) => {
        activeFieldRef.current = { kind: 'option', index, el };
    }, []);

    const insertVariable = useCallback((placeholder: string) => {
        const token = `{${placeholder}}`;
        const { kind, index, el } = activeFieldRef.current;
        const current = kind === 'option' ? (options[index]?.text ?? '') : question;
        const start = el?.selectionStart ?? current.length;
        const end = el?.selectionEnd ?? current.length;
        const next = current.slice(0, start) + token + current.slice(end);

        if (kind === 'option' && index >= 0) {
            handleOptionChange(index, next);
        } else {
            handleSetLocalPollState((prev: Poll) => ({ ...prev, question: next }));
        }

        // Restore caret just after the inserted token.
        const caret = start + token.length;
        requestAnimationFrame(() => {
            if (el) {
                try { el.focus(); el.setSelectionRange(caret, caret); } catch { /* noop */ }
            }
        });
    }, [options, question, handleOptionChange, handleSetLocalPollState]);

    // Effects
    useEffect(() => {
        if (pollFromWAModal && handleSetLocalPollState) {
            handleSetLocalPollState(pollFromWAModal);
        }
    }, [pollFromWAModal, handleSetLocalPollState]);

    return (
        <Stack spacing={3} sx={styles.container} className="bt-poll-editor">
            {!disabled && editorContext === 'scheduleCampaign' && <TemplatePlaceholderBar onInsert={insertVariable} />}
            <QuestionInput
                question={question}
                onChange={handleQuestionChange}
                onFieldFocus={captureQuestion}
                disabled={disabled}
            />
            <OptionsContainer
                options={options}
                duplicateIndices={duplicateIndices}
                onOptionChange={handleOptionChange}
                onOptionFocus={captureOption}
                onDragEnd={handleDragEnd}
                disabled={disabled}
            />
            <Footer
                allowMultiple={allowMultiple}
                hasDuplicates={hasDuplicateOptions}
                onAllowMultipleChange={handleAllowMultipleChange}
                disabled={disabled}
            />
        </Stack>
    );
};

CreatePoll.displayName = 'CreatePoll';

export default memo(CreatePoll);